// types for the service area data
export interface CityImageSet {
  hero: string;
  aboutImage: string;
  terrazzoBeforeAfter: string;
  neighborhoodMap: string;
}

export interface DataTableRow {
  dataPoint: string;
  figure: string;
  meaning: string;
}

export interface PricingRow {
  service: string;
  range: string;
  factors: string;
}

export interface FAQItem {
  q: string;
  a: string;
}

export interface CityContent {
  intro: {
    paragraph1: string;
    paragraph2: string;
  };
  statsTitle: string;
  statsIntro: string;
  dataTable: DataTableRow[];
  // dataNote: string;
  signs: {
    title: string;
    text: string;
  };
  considerations: {
    title: string;
    text: string;
  };
  services: string;
  pricing: {
    title: string;
    // note: string;
    rows: PricingRow[];
  };
  faq: {
    title: string;
    items: FAQItem[];
  };
  contact: {
    businessName: string;
    address: string;
    phone: string;
    website: string;
    serviceArea: string;
    mapCenter: string;
  };
  structuredData: {
    note: string;
    schema: Record<string, unknown>;
  };
  // Some cities have a customer quote placeholder
  quote?: {
    text: string;
    author: string;
    note: string;
  };
}

export interface CityData {
  slug: string;
  name: string;
  state: string;
  meta: {
    lastUpdated: string;
    reviewer: string;
  };
  images: CityImageSet;
  content: CityContent;
}

export const serviceAreas: CityData[] = [
  {
    slug: "tampa",
    name: "Tampa",
    state: "FL",
    meta: {
      lastUpdated: "August 2026",
      reviewer:
        "[INSERT REVIEWER NAME], Owner / Lead Technician -- 48 Years in Terrazzo Restoration",
    },
    images: {
      hero: "tampa-hero.jpg",
      aboutImage: "tampa-abt.jpg",
      terrazzoBeforeAfter: "tampa-terrazzo-floors.jpg",
      neighborhoodMap: "tampa-neighborhoods-map.png",
    },
    content: {
      intro: {
        paragraph1: `Tampa is where Tampa Terrazzo does business every day -- from the historic bungalows of Hyde Park and Seminole Heights to the mid-century ranch homes spread across South Tampa, and the modern high-rises and commercial properties downtown and along the Riverwalk. Few cities in Florida carry as much of the original mid-century terrazzo Tampa Bay Area is known for, and we've spent 48 years restoring it, alongside marble entryways, travertine patios, and concrete driveways across every era of Tampa construction.`,
        paragraph2: `What we see most in Tampa: original poured terrazzo in older South Tampa homes, marble and travertine in historic-district renovations, and concrete/hardscaping work for the newer construction filling in around the city's core.`,
      },
      statsTitle: "Tampa by the Numbers: What the Housing Data Tells Us",
      statsIntro: `Median home construction year is the clearest single indicator of how much original, restorable terrazzo a market is likely to hold -- Florida's terrazzo-pour era ran roughly from the 1950s through the mid-1970s. Here's how that plays out across Tampa, using the most current U.S. Census / American Community Survey-derived figures available.`,
      dataTable: [
        {
          dataPoint: "Median home construction year",
          figure: "1985",
          meaning: "Terrazzo-era stock present but less concentrated",
        },
        {
          dataPoint: "Share of homes built pre-1950",
          figure: "~13.1%",
          meaning: "Higher share = greater density of original poured terrazzo",
        },
        {
          dataPoint: "Total housing units",
          figure: "182,425",
          meaning: "Market size for restoration and hardscaping demand",
        },
        {
          dataPoint: "Population",
          figure: "~400,000+ (city)",
          meaning: "General market sizing context",
        },
      ],
      // dataNote: `Highest concentration in South Tampa, Hyde Park, and Seminole Heights, where original construction dates run well below the citywide median.`,
      signs: {
        title: "Signs Your Tampa Home Might Have Original Terrazzo",
        text: `Tampa's older neighborhoods -- South Tampa, Seminole Heights, Palma Ceia, and the areas surrounding Bayshore Boulevard -- saw heavy construction during the same post-war boom that left much of Florida with poured terrazzo as a standard flooring choice. Citywide, Tampa's median home was built in 1985, but that number is pulled upward by decades of newer development; pockets like South Tampa and Seminole Heights run well below it. If your home dates to the 1950s or 60s and currently has carpet, vinyl, or later tile throughout, there's a real chance original terrazzo is still underneath, especially in homes that haven't been fully renovated since original construction.`,
      },
      considerations: {
        title: "Tampa-Specific Considerations",
        text: `Downtown and Westshore commercial properties often need terrazzo and stone restoration scheduled around business hours to minimize disruption, something we regularly coordinate for office lobbies and retail spaces. Residential projects near Bayshore Boulevard and other waterfront areas also warrant the same salt-air sealing considerations that apply throughout Tampa Bay Area's coastal-adjacent neighborhoods.`,
      },
      services: `Terrazzo - Marble - Travertine - Concrete - Hardscaping -- full cleaning, repair, sealing, and polishing for each.`,
      pricing: {
        title: "Typical Investment Range -- Tampa (2026)",
        rows: [
          {
            service: "Terrazzo restoration",
            range: "$5-$12 per sq ft",
            factors: "Condition, aggregate exposure needed, gloss level",
          },
          {
            service: "Marble restoration",
            range: "$4-$10 per sq ft",
            factors: "Etching severity, polish level, sealing",
          },
          {
            service: "Travertine restoration",
            range: "$3-$7 per sq ft",
            factors: "Pitting/hole-filling needs, indoor vs. outdoor",
          },
          {
            service: "Concrete sealing/repair",
            range: "$1-$3 per sq ft sealing; $3-$7 per sq ft if resurfacing",
            factors: "Crack repair, staining, sealant type",
          },
          {
            service: "Paver/hardscaping maintenance",
            range: "$1.25-$3.50 per sq ft",
            factors: "Joint-sand replacement, sealing, cleaning",
          },
        ],
      },
      faq: {
        title: "Tampa Questions We Hear Most Often",
        items: [
          {
            q: "Does Tampa Terrazzo serve properties in downtown Tampa and South Tampa specifically?",
            a: "Yes -- Tampa is our home service area, and we work throughout the city, from historic districts to newer commercial development.",
          },
          {
            q: "Can you work around business hours for a downtown commercial property?",
            a: "Yes -- we regularly schedule commercial restoration projects after hours or in phases to minimize disruption to an operating business.",
          },
          {
            q: "Is my South Tampa home likely to have original terrazzo?",
            a: "If it was built in the 1950s or 60s, especially in the neighborhoods surrounding Bayshore Boulevard or Palma Ceia, there's a good chance the original floor is still there under later flooring.",
          },
          {
            q: "How quickly can you respond to a project inquiry in Tampa?",
            a: "We maintain responsive scheduling with a same-day average response time throughout Tampa.",
          },
          {
            q: "Why is Tampa's citywide median construction year (1985) newer than St. Petersburg's, if both cities grew up in the same era?",
            a: "Tampa's city limits include large areas of newer development (New Tampa, Westshore-area growth, downtown high-rises) that didn't exist until decades later, which pulls the citywide average later even though pockets like South Tampa and Seminole Heights are just as old as St. Petersburg's historic districts.",
          },
        ],
      },
      contact: {
        businessName: "Tampa Terrazzo",
        address: "1409 Sunnyhills Dr, Brandon, FL 33511",
        phone: "(813) 842-2255",
        website: "tampaterrazzo.com",
        serviceArea: "Tampa, FL and surrounding neighborhoods",
        mapCenter: "27.9506, -82.4572",
      },
      structuredData: {
        note: `Paste the FAQPage schema below into the <head> or end of the <body> of the live Tampa page, unedited except for any wording changes made to the FAQ content above (the schema must match the visible text). This is what allows Google, AI Overviews, and answer engines like ChatGPT or Perplexity to lift these Q&As directly into results.`,
        schema: {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "Does Tampa Terrazzo serve properties in downtown Tampa and South Tampa specifically?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes -- Tampa is our home service area, and we work throughout the city, from historic districts to newer commercial development.",
              },
            },
            {
              "@type": "Question",
              name: "Can you work around business hours for a downtown commercial property?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes -- we regularly schedule commercial restoration projects after hours or in phases to minimize disruption to an operating business.",
              },
            },
            {
              "@type": "Question",
              name: "Is my South Tampa home likely to have original terrazzo?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "If it was built in the 1950s or 60s, especially in the neighborhoods surrounding Bayshore Boulevard or Palma Ceia, there's a good chance the original floor is still there under later flooring.",
              },
            },
            {
              "@type": "Question",
              name: "How quickly can you respond to a project inquiry in Tampa?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "We maintain responsive scheduling with a same-day average response time throughout Tampa.",
              },
            },
            {
              "@type": "Question",
              name: "Why is Tampa's citywide median construction year (1985) newer than St. Petersburg's, if both cities grew up in the same era?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Tampa's city limits include large areas of newer development (New Tampa, Westshore-area growth, downtown high-rises) that didn't exist until decades later, which pulls the citywide average later even though pockets like South Tampa and Seminole Heights are just as old as St. Petersburg's historic districts.",
              },
            },
          ],
        },
      },
    },
  },
  {
    slug: "st-petersburg",
    name: "St. Petersburg",
    state: "FL",
    meta: {
      lastUpdated: "August 2026",
      reviewer:
        "[INSERT REVIEWER NAME], Owner / Lead Technician -- 48 Years in Terrazzo Restoration",
    },
    images: {
      hero: "st-pete-hero.jpg",
      aboutImage: "st-pete.jpg",
      terrazzoBeforeAfter: "st-pete-terrazzo-uncovered.jpg",
      neighborhoodMap: "st-pete-districts-map.png",
    },
    content: {
      intro: {
        paragraph1: `St. Petersburg holds one of the highest concentrations of 1950s-era housing of any city in the country -- which means an unusually high share of St. Pete homes still have original terrazzo underneath later flooring, particularly in the historic districts like Old Northeast, Kenwood, and Historic Roser Park. We've restored terrazzo, marble, and travertine throughout these neighborhoods for decades, and increasingly work on the concrete driveways and paver patios of the city's newer waterfront development as well.`,
        paragraph2: `What we see most in St. Petersburg: original mid-century terrazzo in historic-district bungalows, travertine and marble in updated bathrooms and entries, and paver/hardscaping work along the waterfront.`,
      },
      statsTitle:
        "St. Petersburg Housing Snapshot: Why the Numbers Matter Here",
      statsIntro: `Of the nine markets we serve, St. Petersburg's housing data makes the strongest case for original terrazzo being present. The figures below, drawn from the latest available U.S. Census / American Community Survey data, show why we treat this city differently from the rest of our service area.`,
      dataTable: [
        {
          dataPoint: "Median home construction year",
          figure: "1969",
          meaning: "High likelihood of original terrazzo citywide",
        },
        {
          dataPoint: "Share of homes built pre-1950",
          figure: "~14.3%",
          meaning: "Higher share = greater density of original poured terrazzo",
        },
        {
          dataPoint: "Total housing units",
          figure: "119,244",
          meaning: "Market size for restoration and hardscaping demand",
        },
        {
          dataPoint: "Population",
          figure: "262,732",
          meaning: "General market sizing context",
        },
      ],
      // dataNote: `Lowest median construction year of all 9 service areas -- the strongest single indicator of original terrazzo density in this market.`,
      signs: {
        title: "Why St. Petersburg's Housing Stock Is Different",
        text: `St. Petersburg's citywide median home was built in 1969 -- the earliest median construction year of any of our 9 service areas, and squarely inside Florida's terrazzo-pour era. In the Historic Old Northeast neighborhood specifically, the median construction year drops even further, to 1964, with roughly a third of homes built before 1950. St. Pete's historic districts weren't just built during the mid-century terrazzo era -- many of them have stayed remarkably intact, with original architectural details preserved even where flooring was later covered. This is part of why we treat St. Petersburg as one of our highest-priority areas for the "terrazzo under carpet" assessment: a bungalow in Old Northeast or Kenwood that's never had its original flooring uncovered is a common, not rare, situation here.`,
      },
      considerations: {
        title: "St. Petersburg-Specific Considerations",
        text: `Downtown St. Pete's growing condo and waterfront development means marble and travertine work increasingly involves high-rise units, where equipment access and building coordination differ from a standalone home. We also see more paver hardscaping work here than in Tampa Bay Area's older, more inland communities, reflecting the city's newer waterfront and mixed-use development.`,
      },
      services: `Terrazzo - Marble - Travertine - Concrete - Hardscaping.`,
      pricing: {
        title: "Typical Investment Range -- St. Petersburg (2026)",
        rows: [
          {
            service: "Terrazzo restoration",
            range: "$5-$12 per sq ft",
            factors: "Condition, aggregate exposure needed, gloss level",
          },
          {
            service: "Marble restoration",
            range: "$4-$10 per sq ft",
            factors: "Etching severity, polish level, sealing",
          },
          {
            service: "Travertine restoration",
            range: "$3-$7 per sq ft",
            factors: "Pitting/hole-filling needs, indoor vs. outdoor",
          },
          {
            service: "Concrete sealing/repair",
            range: "$1-$3 per sq ft sealing; $3-$7 per sq ft if resurfacing",
            factors: "Crack repair, staining, sealant type",
          },
          {
            service: "Paver/hardscaping maintenance",
            range: "$1.25-$3.50 per sq ft",
            factors: "Joint-sand replacement, sealing, cleaning",
          },
        ],
      },
      faq: {
        title: "Common Questions From St. Petersburg Homeowners",
        items: [
          {
            q: "Is my St. Petersburg home likely to have original terrazzo?",
            a: "If it was built in the 1950s or 60s -- common across St. Pete's historic districts -- there's a strong chance the original floor is still there under later flooring. Citywide, more than half of St. Petersburg's housing stock predates 1970.",
          },
          {
            q: "Do you work on condos and high-rise units in downtown St. Petersburg?",
            a: "Yes -- we coordinate with building management as needed for marble, travertine, and terrazzo restoration in condo and high-rise settings.",
          },
          {
            q: "What neighborhoods in St. Petersburg do you serve most often?",
            a: "Old Northeast, Kenwood, Historic Roser Park, and the surrounding historic districts, along with newer downtown and waterfront development.",
          },
          {
            q: "How do I find out if my St. Pete home has original terrazzo without tearing up my floors?",
            a: "A free on-site assessment can often identify strong signs of original terrazzo (subfloor height differences, transition strips, construction-era records) before any flooring is removed.",
          },
          {
            q: "How does St. Petersburg's housing age compare to the rest of Tampa Bay Area?",
            a: "St. Petersburg has the oldest median home construction year of all nine areas we serve (1969), making it statistically the highest-probability market in our entire service area for original, restorable terrazzo.",
          },
        ],
      },
      contact: {
        businessName: "Tampa Terrazzo",
        address: "1409 Sunnyhills Dr, Brandon, FL 33511",
        phone: "(813) 842-2255",
        website: "tampaterrazzo.com",
        serviceArea: "St. Petersburg, FL and surrounding neighborhoods",
        mapCenter: "27.7676, -82.6403",
      },
      structuredData: {
        note: `Paste the FAQPage schema below into the <head> or end of the <body> of the live St. Petersburg page, unedited except for any wording changes made to the FAQ content above (the schema must match the visible text). This is what allows Google, AI Overviews, and answer engines like ChatGPT or Perplexity to lift these Q&As directly into results.`,
        schema: {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "Is my St. Petersburg home likely to have original terrazzo?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "If it was built in the 1950s or 60s -- common across St. Pete's historic districts -- there's a strong chance the original floor is still there under later flooring. Citywide, more than half of St. Petersburg's housing stock predates 1970.",
              },
            },
            {
              "@type": "Question",
              name: "Do you work on condos and high-rise units in downtown St. Petersburg?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes -- we coordinate with building management as needed for marble, travertine, and terrazzo restoration in condo and high-rise settings.",
              },
            },
            {
              "@type": "Question",
              name: "What neighborhoods in St. Petersburg do you serve most often?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Old Northeast, Kenwood, Historic Roser Park, and the surrounding historic districts, along with newer downtown and waterfront development.",
              },
            },
            {
              "@type": "Question",
              name: "How do I find out if my St. Pete home has original terrazzo without tearing up my floors?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "A free on-site assessment can often identify strong signs of original terrazzo (subfloor height differences, transition strips, construction-era records) before any flooring is removed.",
              },
            },
            {
              "@type": "Question",
              name: "How does St. Petersburg's housing age compare to the rest of Tampa Bay Area?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "St. Petersburg has the oldest median home construction year of all nine areas we serve (1969), making it statistically the highest-probability market in our entire service area for original, restorable terrazzo.",
              },
            },
          ],
        },
      },
    },
  },
  // Clearwater
  {
    slug: "clearwater",
    name: "Clearwater",
    state: "FL",
    meta: {
      lastUpdated: "August 2026",
      reviewer:
        "[INSERT REVIEWER NAME], Owner / Lead Technician -- 48 Years in Terrazzo Restoration",
    },
    images: {
      hero: "clearwater-hero.jpg",
      aboutImage: "clearwater-abt.jpg",
      terrazzoBeforeAfter: "clearwater-terrazzo.jpg",
      neighborhoodMap: "clearwater-map.png",
    },
    content: {
      intro: {
        paragraph1: `Clearwater's coastal position means salt air and near-constant humidity are part of daily life for any exterior surface -- travertine pool decks, marble entries, and concrete driveways all wear faster here than in more inland parts of Tampa Bay Area. We account for that in every Clearwater project, from penetrating sealers built for salt-air exposure to breathable products that won't trap moisture in humid beachside homes.`,
        paragraph2: `What we see most in Clearwater: travertine and paver pool decks needing more frequent resealing due to coastal exposure, marble in condo and waterfront properties, and terrazzo in the city's older residential neighborhoods further from the immediate coastline.`,
      },
      // quote: {
      //     text: `"[PLACEHOLDER -- replace with a real Clearwater customer quote, ideally naming the neighborhood and service performed.]"`,
      //     author: `[Customer Name], [Neighborhood], Clearwater`,
      //     note: `This is placeholder content only -- do not publish a fabricated testimonial. Replace with an actual review from a Clearwater customer (pulled from Google Business Profile, Facebook, or a direct request) before this page goes live. Once real reviews are in place, consider adding Review or AggregateRating schema alongside them.`,
      // },
      statsTitle: "Clearwater's Housing Profile in Context",
      statsIntro: `Clearwater's building era sits right at the edge of Florida's terrazzo-pour years, which shapes how we approach every assessment here. The figures below come from the most recent available U.S. Census / American Community Survey-derived data for the city.`,
      dataTable: [
        {
          dataPoint: "Median home construction year",
          figure: "1978",
          meaning: "Terrazzo-era stock present but less concentrated",
        },
        {
          dataPoint: "Share of homes built pre-1950",
          figure: "~4.4%",
          meaning: "Higher share = greater density of original poured terrazzo",
        },
        {
          dataPoint: "Total housing units",
          figure: "61,123",
          meaning: "Market size for restoration and hardscaping demand",
        },
        {
          dataPoint: "Population",
          figure: "117,247",
          meaning: "General market sizing context",
        },
      ],
      // dataNote: `Falls right at the tail end of Florida's typical terrazzo-pour era; older sections skew earlier than the citywide median.`,
      signs: {
        title: "Signs Coastal Exposure Is Affecting Your Clearwater Property",
        text: `Clearwater's citywide median home was built in 1978, right at the tail end of Florida's typical terrazzo-pour era -- meaning original terrazzo is real here but less densely concentrated than in St. Petersburg or Largo. Homes and condos within a few miles of Clearwater Beach or the intracoastal waterway typically show sealer breakdown faster than properties further inland -- watch for water no longer beading on travertine or concrete surfaces, chalky or faded appearance on outdoor sealed surfaces, and more frequent efflorescence on concrete pool decks. These are all signs that coastal humidity and salt air have worked through a sealer faster than its rated lifespan would suggest inland.`,
      },
      considerations: {
        title: "Clearwater-Specific Considerations",
        text: `Clearwater Beach and the barrier-island communities see the most aggressive climate exposure in our entire service area, which is why we typically recommend penetrating sealers over topical products for any outdoor surface here -- travertine pool decks and paver patios in these areas benefit from resealing on the more frequent end of our recommended intervals. Inland Clearwater neighborhoods, further from the immediate coast, see wear closer to Tampa Bay Area's general average.`,
      },
      services: `Terrazzo - Marble - Travertine - Concrete - Hardscaping.`,
      pricing: {
        title: "Typical Investment Range -- Clearwater (2026)",
        rows: [
          {
            service: "Terrazzo restoration",
            range: "$5-$12 per sq ft",
            factors: "Condition, aggregate exposure needed, gloss level",
          },
          {
            service: "Marble restoration",
            range: "$4-$10 per sq ft",
            factors: "Etching severity, polish level, sealing",
          },
          {
            service: "Travertine restoration",
            range: "$3-$7 per sq ft",
            factors: "Pitting/hole-filling needs, indoor vs. outdoor",
          },
          {
            service: "Concrete sealing/repair",
            range: "$1-$3 per sq ft sealing; $3-$7 per sq ft if resurfacing",
            factors: "Crack repair, staining, sealant type",
          },
          {
            service: "Paver/hardscaping maintenance",
            range: "$1.25-$3.50 per sq ft",
            factors: "Joint-sand replacement, sealing, cleaning",
          },
        ],
      },
      faq: {
        title: "Clearwater Homeowners Ask Us This Often",
        items: [
          {
            q: "Does coastal salt air really affect how often I need to reseal?",
            a: "Yes -- homes near Clearwater's immediate coastline typically need more frequent resealing than inland Tampa Bay Area properties, due to combined salt and humidity exposure.",
          },
          {
            q: "Do you service Clearwater Beach specifically, or just the mainland?",
            a: "We serve both -- Clearwater Beach and barrier-island properties, along with mainland Clearwater neighborhoods.",
          },
          {
            q: "What sealer do you recommend for a Clearwater pool deck?",
            a: "Generally a penetrating sealer rather than a topical product, since it holds up better against salt air, humidity, and standing pool water without peeling.",
          },
          {
            q: "How often should outdoor travertine be resealed in Clearwater specifically?",
            a: "More frequently than Tampa Bay Area's general recommendation -- properties near the immediate coastline should plan for the more frequent end of our standard resealing interval.",
          },
          {
            q: "Is Clearwater's housing stock old enough to likely have original terrazzo?",
            a: "Citywide, Clearwater's median home dates to 1978 -- right at the edge of Florida's terrazzo era, so original terrazzo exists but isn't as universally common as in St. Petersburg; older, established sections of the city are the best candidates.",
          },
        ],
      },
      contact: {
        businessName: "Tampa Terrazzo",
        address: "1409 Sunnyhills Dr, Brandon, FL 33511",
        phone: "(813) 842-2255",
        website: "tampaterrazzo.com",
        serviceArea: "Clearwater, FL and surrounding neighborhoods",
        mapCenter: "27.9659, -82.8001",
      },
      structuredData: {
        note: `Paste the FAQPage schema below into the <head> or end of the <body> of the live Clearwater page, unedited except for any wording changes made to the FAQ content above (the schema must match the visible text).`,
        schema: {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "Does coastal salt air really affect how often I need to reseal?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes -- homes near Clearwater's immediate coastline typically need more frequent resealing than inland Tampa Bay Area properties, due to combined salt and humidity exposure.",
              },
            },
            {
              "@type": "Question",
              name: "Do you service Clearwater Beach specifically, or just the mainland?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "We serve both -- Clearwater Beach and barrier-island properties, along with mainland Clearwater neighborhoods.",
              },
            },
            {
              "@type": "Question",
              name: "What sealer do you recommend for a Clearwater pool deck?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Generally a penetrating sealer rather than a topical product, since it holds up better against salt air, humidity, and standing pool water without peeling.",
              },
            },
            {
              "@type": "Question",
              name: "How often should outdoor travertine be resealed in Clearwater specifically?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "More frequently than Tampa Bay Area's general recommendation -- properties near the immediate coastline should plan for the more frequent end of our standard resealing interval.",
              },
            },
            {
              "@type": "Question",
              name: "Is Clearwater's housing stock old enough to likely have original terrazzo?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Citywide, Clearwater's median home dates to 1978 -- right at the edge of Florida's terrazzo era, so original terrazzo exists but isn't as universally common as in St. Petersburg; older, established sections of the city are the best candidates.",
              },
            },
          ],
        },
      },
    },
  },
  // Brandon
  {
    slug: "brandon",
    name: "Brandon",
    state: "FL",
    meta: {
      lastUpdated: "August 2026",
      reviewer:
        "[INSERT REVIEWER NAME], Owner / Lead Technician -- 48 Years in Terrazzo Restoration",
    },
    images: {
      hero: "brandon-hero.jpg",
      aboutImage: "brandon-abt.jpg",
      terrazzoBeforeAfter: "brandon-terrazzo.jpg",
      neighborhoodMap: "brandon-map.png",
    },
    content: {
      intro: {
        paragraph1: `Brandon is home base for Tampa Terrazzo -- our shop sits right here at 1409 Sunnyhills Dr, which means Brandon and the surrounding area get some of our fastest response times across all five specialties. Brandon's mix of established mid-century subdivisions and newer suburban development means we see everything here: original terrazzo in older homes, and concrete driveways, patios, and paver hardscaping in the newer construction that's filled in around the area over the past two decades.`,
        paragraph2: `What we see most in Brandon: a genuine mix of original terrazzo restoration and newer concrete/hardscaping work, reflecting Brandon's decades of continuous residential growth.`,
      },
      // quote: {
      //     text: `"[PLACEHOLDER -- replace with a real Brandon customer quote, ideally naming the neighborhood and service performed.]"`,
      //     author: `[Customer Name], [Neighborhood], Brandon`,
      //     note: `This is placeholder content only -- do not publish a fabricated testimonial. Replace with an actual review from a Brandon customer (pulled from Google Business Profile, Facebook, or a direct request) before this page goes live. Once real reviews are in place, consider adding Review or AggregateRating schema alongside them.`,
      // },
      statsTitle: "Brandon Housing Data at a Glance",
      statsIntro: `As our home base, Brandon is a market we know from daily, on-the-ground experience as much as from the numbers -- but the housing data below still helps frame what kind of restoration work is most common where, once exact figures are confirmed at the county level.`,
      dataTable: [
        {
          dataPoint: "Median home construction year",
          figure: "1988",
          meaning: "Terrazzo-era stock present but less concentrated",
        },
        {
          dataPoint: "Share of homes built pre-1950",
          figure: "~0.6%",
          meaning: "Higher share = greater density of original poured terrazzo",
        },
        {
          dataPoint: "Total housing units",
          figure: "48,953",
          meaning: "Market size for restoration and hardscaping demand",
        },
        {
          dataPoint: "Population",
          figure: "~119,338 (CDP, est.)",
          meaning: "General market sizing context",
        },
      ],
      // dataNote: `Home base for Tampa Terrazzo. Older, established sections near the original town center predate the newer subdivisions built over the past two decades.`,
      signs: {
        title: "Why Being Local Matters for Brandon Projects",
        text: `Because our shop is based here, Brandon projects benefit from shorter travel time for both estimates and follow-up visits, and we're often able to accommodate same-day or next-day assessments that require more scheduling lead time in farther-out service areas. It also means we've worked in many of Brandon's specific subdivisions repeatedly over the decades, giving us direct familiarity with how different construction eras in the area have held up. Note: exact median-construction-year and pre-1950 housing-share figures for the Brandon CDP were not available from the sources checked during this session -- see the data-sourcing note at the front of this document.`,
      },
      considerations: {
        title: "Brandon-Specific Considerations",
        text: `Brandon's older sections (closer to the original town center) are more likely to have original terrazzo, while the newer subdivisions built over the past 15-20 years toward the eastern edge of the city are more likely to need concrete driveway sealing, crack repair, and paver patio maintenance rather than terrazzo restoration.`,
      },
      services: `Terrazzo - Marble - Travertine - Concrete - Hardscaping.`,
      pricing: {
        title: "Typical Investment Range -- Brandon (2026)",
        rows: [
          {
            service: "Terrazzo restoration",
            range: "$5-$12 per sq ft",
            factors: "Condition, aggregate exposure needed, gloss level",
          },
          {
            service: "Marble restoration",
            range: "$4-$10 per sq ft",
            factors: "Etching severity, polish level, sealing",
          },
          {
            service: "Travertine restoration",
            range: "$3-$7 per sq ft",
            factors: "Pitting/hole-filling needs, indoor vs. outdoor",
          },
          {
            service: "Concrete sealing/repair",
            range: "$1-$3 per sq ft sealing; $3-$7 per sq ft if resurfacing",
            factors: "Crack repair, staining, sealant type",
          },
          {
            service: "Paver/hardscaping maintenance",
            range: "$1.25-$3.50 per sq ft",
            factors: "Joint-sand replacement, sealing, cleaning",
          },
        ],
      },
      faq: {
        title: "Answers to Brandon's Most Common Questions",
        items: [
          {
            q: "Since you're based in Brandon, do you offer faster response times here?",
            a: "Yes -- as our home service area, Brandon and immediately surrounding neighborhoods typically see our fastest scheduling and response times.",
          },
          {
            q: "Do older parts of Brandon have original terrazzo floors?",
            a: "Often, yes -- homes in Brandon's older, more established sections are more likely to have original terrazzo than newer subdivisions built in the past couple of decades.",
          },
          {
            q: "Can you provide same-day estimates in Brandon?",
            a: "In many cases, yes, given our proximity -- call to check current availability for your specific timeline.",
          },
          {
            q: "What's the most common service you provide in Brandon?",
            a: "A genuine mix -- we see roughly equal demand for terrazzo restoration in older homes and concrete/hardscaping work in newer construction.",
          },
        ],
      },
      contact: {
        businessName: "Tampa Terrazzo",
        address: "1409 Sunnyhills Dr, Brandon, FL 33511",
        phone: "(813) 842-2255",
        website: "tampaterrazzo.com",
        serviceArea: "Brandon, FL and surrounding neighborhoods",
        mapCenter: "27.9378, -82.2859",
      },
      structuredData: {
        note: `Paste the FAQPage schema below into the <head> or end of the <body> of the live Brandon page.`,
        schema: {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "Since you're based in Brandon, do you offer faster response times here?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes -- as our home service area, Brandon and immediately surrounding neighborhoods typically see our fastest scheduling and response times.",
              },
            },
            {
              "@type": "Question",
              name: "Do older parts of Brandon have original terrazzo floors?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Often, yes -- homes in Brandon's older, more established sections are more likely to have original terrazzo than newer subdivisions built in the past couple of decades.",
              },
            },
            {
              "@type": "Question",
              name: "Can you provide same-day estimates in Brandon?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "In many cases, yes, given our proximity -- call to check current availability for your specific timeline.",
              },
            },
            {
              "@type": "Question",
              name: "What's the most common service you provide in Brandon?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "A genuine mix -- we see roughly equal demand for terrazzo restoration in older homes and concrete/hardscaping work in newer construction.",
              },
            },
          ],
        },
      },
    },
  },
  // Largo
  {
    slug: "largo",
    name: "Largo",
    state: "FL",
    meta: {
      lastUpdated: "August 2026",
      reviewer:
        "[INSERT REVIEWER NAME], Owner / Lead Technician -- 48 Years in Terrazzo Restoration",
    },
    images: {
      hero: "largo-hero.jpg",
      aboutImage: "largo-abt.jpg",
      terrazzoBeforeAfter: "largo-terrazzo.jpg",
      neighborhoodMap: "largo-map.png",
    },
    content: {
      intro: {
        paragraph1: `Largo is one of Pinellas County's most established mid-century communities, developed heavily during the same post-war boom that left St. Petersburg and Clearwater with so much original terrazzo -- and Largo is no exception. We regularly uncover original terrazzo floors in Largo homes that have been carpeted over for decades, alongside the marble and travertine common in the area's bathroom and entryway renovations.`,
        paragraph2: `What we see most in Largo: original mid-century terrazzo restoration, plus marble and travertine work in older homes throughout the city's established residential neighborhoods.`,
      },
      // quote: {
      //     text: `"[PLACEHOLDER -- replace with a real Largo customer quote, ideally naming the neighborhood and service performed.]"`,
      //     author: `[Customer Name], [Neighborhood], Largo`,
      //     note: `This is placeholder content only -- do not publish a fabricated testimonial. Replace with an actual review from a Largo customer (pulled from Google Business Profile, Facebook, or a direct request) before this page goes live. Once real reviews are in place, consider adding Review or AggregateRating schema alongside them.`,
      // },
      statsTitle: "What Largo's Housing Numbers Reveal",
      statsIntro: `Largo grew up during the same mid-century building wave as much of Pinellas County, and its housing data reflects that. The figures below draw on the most recent available U.S. Census / American Community Survey-derived data for the city.`,
      dataTable: [
        {
          dataPoint: "Median home construction year",
          figure: "1978",
          meaning: "Terrazzo-era stock present but less concentrated",
        },
        {
          dataPoint: "Share of homes built pre-1950",
          figure: "~1.8%",
          meaning: "Higher share = greater density of original poured terrazzo",
        },
        {
          dataPoint: "Total housing units",
          figure: "46,806",
          meaning: "Market size for restoration and hardscaping demand",
        },
        {
          dataPoint: "Population",
          figure: "~83,000 (est.)",
          meaning: "General market sizing context",
        },
      ],
      // dataNote: `Median year sits at the boundary of the terrazzo era -- original stock is real but less concentrated than St. Petersburg.`,
      signs: {
        title: "What Makes Largo's Terrazzo Distinct",
        text: `Largo's citywide median home construction year is 1978, tied with Clearwater for the tail end of Florida's terrazzo-pour era. Largo's mid-century development happened in a fairly concentrated building boom, which means many of its terrazzo installations share similar aggregate styles and installation eras -- useful context when we're color- and pattern-matching a repair, since we've worked on enough Largo-era terrazzo to recognize common patterns quickly during an assessment.`,
      },
      considerations: {
        title: "Largo-Specific Considerations",
        text: `As an inland Pinellas County community, Largo sees less of the direct salt-air exposure that affects Clearwater's beachfront properties, but still benefits from Florida's general humidity-aware sealing approach. Largo's established tree canopy in many neighborhoods also means more shaded patios and walkways, which can accelerate mold and algae growth on concrete and pavers compared to full-sun properties.`,
      },
      services: `Terrazzo - Marble - Travertine - Concrete - Hardscaping.`,
      pricing: {
        title: "Typical Investment Range -- Largo (2026)",
        rows: [
          {
            service: "Terrazzo restoration",
            range: "$5-$12 per sq ft",
            factors: "Condition, aggregate exposure needed, gloss level",
          },
          {
            service: "Marble restoration",
            range: "$4-$10 per sq ft",
            factors: "Etching severity, polish level, sealing",
          },
          {
            service: "Travertine restoration",
            range: "$3-$7 per sq ft",
            factors: "Pitting/hole-filling needs, indoor vs. outdoor",
          },
          {
            service: "Concrete sealing/repair",
            range: "$1-$3 per sq ft sealing; $3-$7 per sq ft if resurfacing",
            factors: "Crack repair, staining, sealant type",
          },
          {
            service: "Paver/hardscaping maintenance",
            range: "$1.25-$3.50 per sq ft",
            factors: "Joint-sand replacement, sealing, cleaning",
          },
        ],
      },
      faq: {
        title: "Largo Homeowner FAQ",
        items: [
          {
            q: "Is terrazzo restoration common in Largo specifically?",
            a: "Yes -- Largo's mid-century development era means original terrazzo is common throughout the city's established neighborhoods, often still intact under later flooring.",
          },
          {
            q: "Does Largo's inland location mean less sealing maintenance than Clearwater?",
            a: "Generally yes for salt-air exposure specifically, though standard Florida humidity considerations still apply to sealing schedules.",
          },
          {
            q: "Why do shaded patios in Largo need more frequent cleaning?",
            a: "Heavy tree canopy common in Largo's established neighborhoods creates more shade and moisture retention, which accelerates mold and algae growth on concrete and paver surfaces.",
          },
          {
            q: "Can you match the specific aggregate style common in Largo's terrazzo?",
            a: "In most cases, yes -- we've worked extensively with the aggregate styles typical of Largo's mid-century construction era.",
          },
        ],
      },
      contact: {
        businessName: "Tampa Terrazzo",
        address: "1409 Sunnyhills Dr, Brandon, FL 33511",
        phone: "(813) 842-2255",
        website: "tampaterrazzo.com",
        serviceArea: "Largo, FL and surrounding neighborhoods",
        mapCenter: "27.9095, -82.7873",
      },
      structuredData: {
        note: `Paste the FAQPage schema below into the <head> or end of the <body> of the live Largo page.`,
        schema: {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "Is terrazzo restoration common in Largo specifically?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes -- Largo's mid-century development era means original terrazzo is common throughout the city's established neighborhoods, often still intact under later flooring.",
              },
            },
            {
              "@type": "Question",
              name: "Does Largo's inland location mean less sealing maintenance than Clearwater?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Generally yes for salt-air exposure specifically, though standard Florida humidity considerations still apply to sealing schedules.",
              },
            },
            {
              "@type": "Question",
              name: "Why do shaded patios in Largo need more frequent cleaning?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Heavy tree canopy common in Largo's established neighborhoods creates more shade and moisture retention, which accelerates mold and algae growth on concrete and paver surfaces.",
              },
            },
            {
              "@type": "Question",
              name: "Can you match the specific aggregate style common in Largo's terrazzo?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "In most cases, yes -- we've worked extensively with the aggregate styles typical of Largo's mid-century construction era.",
              },
            },
          ],
        },
      },
    },
  },
  // Palm Harbor
  {
    slug: "palm-harbor",
    name: "Palm Harbor",
    state: "FL",
    meta: {
      lastUpdated: "August 2026",
      reviewer:
        "[INSERT REVIEWER NAME], Owner / Lead Technician -- 48 Years in Terrazzo Restoration",
    },
    images: {
      hero: "palm-harbor-hero.jpg",
      aboutImage: "palmharbor-abt.jpg",
      terrazzoBeforeAfter: "palmharbor-terrazzo.jpg",
      neighborhoodMap: "palm-harbor-map.png",
    },
    content: {
      intro: {
        paragraph1: `Palm Harbor's mix of established coastal neighborhoods and master-planned golf course communities means we handle a wide range of surfaces here -- from original terrazzo in the area's older homes to the marble, travertine, and paver patios common in its newer luxury developments. Palm Harbor's coastal position along Pinellas County's Gulf side also means the same salt-air sealing considerations that apply throughout the western side of the county.`,
        paragraph2: `What we see most in Palm Harbor: a mix of mid-century terrazzo restoration and newer-construction marble, travertine, and hardscaping work in golf course and waterfront communities.`,
      },
      // quote: {
      //     text: `"[PLACEHOLDER -- replace with a real Palm Harbor customer quote, ideally naming the neighborhood and service performed.]"`,
      //     author: `[Customer Name], [Neighborhood], Palm Harbor`,
      //     note: `This is placeholder content only -- do not publish a fabricated testimonial. Replace with an actual review from a Palm Harbor customer (pulled from Google Business Profile, Facebook, or a direct request) before this page goes live. Once real reviews are in place, consider adding Review or AggregateRating schema alongside them.`,
      // },
      statsTitle: "Palm Harbor Housing Data Overview",
      statsIntro: `Palm Harbor's two-tier development pattern -- older town-center neighborhoods alongside newer planned communities -- makes its housing profile worth a closer look before generalizing about what's under any given floor.`,
      dataTable: [
        {
          dataPoint: "Median home construction year",
          figure: "1974",
          meaning: "Terrazzo-era stock present but less concentrated",
        },
        {
          dataPoint: "Share of homes built pre-1950",
          figure: "2%",
          meaning: "Higher share = greater density of original poured terrazzo",
        },
        {
          dataPoint: "Total housing units",
          figure: "27,154",
          meaning: "Market size for restoration and hardscaping demand",
        },
        {
          dataPoint: "Population",
          figure: "~61,366 (CDP, est.)",
          meaning: "General market sizing context",
        },
      ],
      // dataNote: `Splits between older, established neighborhoods near town center and newer golf-course/waterfront communities.`,
      signs: {
        title: "Palm Harbor's Two Distinct Property Types",
        text: `Palm Harbor genuinely splits into two different restoration profiles: older, established neighborhoods closer to the town center that share Pinellas County's typical mid-century terrazzo history, and newer master-planned golf course and waterfront communities where marble, travertine, and custom paver hardscaping are far more common than original terrazzo. We approach each differently from the first assessment. Note: exact median-construction-year figures for Palm Harbor were not available from the sources checked during this session -- see the data-sourcing note at the front of this document.`,
      },
      considerations: {
        title: "Palm Harbor-Specific Considerations",
        text: `Golf course community homes often feature extensive paver hardscaping around pools and outdoor living spaces, which benefit from the same coastal-aware sealing schedule we recommend elsewhere along Pinellas' Gulf side. Waterfront properties specifically should plan for more frequent resealing intervals given direct exposure to Gulf humidity and salt air.`,
      },
      services: `Terrazzo - Marble - Travertine - Concrete - Hardscaping.`,
      pricing: {
        title: "Typical Investment Range -- Palm Harbor (2026)",
        rows: [
          {
            service: "Terrazzo restoration",
            range: "$5-$12 per sq ft",
            factors: "Condition, aggregate exposure needed, gloss level",
          },
          {
            service: "Marble restoration",
            range: "$4-$10 per sq ft",
            factors: "Etching severity, polish level, sealing",
          },
          {
            service: "Travertine restoration",
            range: "$3-$7 per sq ft",
            factors: "Pitting/hole-filling needs, indoor vs. outdoor",
          },
          {
            service: "Concrete sealing/repair",
            range: "$1-$3 per sq ft sealing; $3-$7 per sq ft if resurfacing",
            factors: "Crack repair, staining, sealant type",
          },
          {
            service: "Paver/hardscaping maintenance",
            range: "$1.25-$3.50 per sq ft",
            factors: "Joint-sand replacement, sealing, cleaning",
          },
        ],
      },
      faq: {
        title: "Questions Palm Harbor Residents Often Ask",
        items: [
          {
            q: "Do you service Palm Harbor's golf course and waterfront communities?",
            a: "Yes -- we regularly work on marble, travertine, and paver installations throughout Palm Harbor's newer planned communities, alongside terrazzo restoration in its older neighborhoods.",
          },
          {
            q: "Is my Palm Harbor home more likely to have terrazzo or newer stone surfaces?",
            a: "It depends on when and where it was built -- older, established neighborhoods closer to town are more likely to have original terrazzo, while newer golf course and waterfront communities typically feature marble, travertine, and paver hardscaping instead.",
          },
          {
            q: "How often should pavers around a Palm Harbor pool be resealed?",
            a: "Generally on the more frequent end of our standard interval, given Gulf-side humidity and salt-air exposure similar to Clearwater's coastal properties.",
          },
          {
            q: "Do you handle large-scale paver hardscaping in master-planned communities?",
            a: "Yes -- we regularly service extensive paver installations common in Palm Harbor's golf course and waterfront developments.",
          },
        ],
      },
      contact: {
        businessName: "Tampa Terrazzo",
        address: "1409 Sunnyhills Dr, Brandon, FL 33511",
        phone: "(813) 842-2255",
        website: "tampaterrazzo.com",
        serviceArea: "Palm Harbor, FL and surrounding neighborhoods",
        mapCenter: "28.0781, -82.7601",
      },
      structuredData: {
        note: `Paste the FAQPage schema below into the <head> or end of the <body> of the live Palm Harbor page.`,
        schema: {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "Do you service Palm Harbor's golf course and waterfront communities?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes -- we regularly work on marble, travertine, and paver installations throughout Palm Harbor's newer planned communities, alongside terrazzo restoration in its older neighborhoods.",
              },
            },
            {
              "@type": "Question",
              name: "Is my Palm Harbor home more likely to have terrazzo or newer stone surfaces?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "It depends on when and where it was built -- older, established neighborhoods closer to town are more likely to have original terrazzo, while newer golf course and waterfront communities typically feature marble, travertine, and paver hardscaping instead.",
              },
            },
            {
              "@type": "Question",
              name: "How often should pavers around a Palm Harbor pool be resealed?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Generally on the more frequent end of our standard interval, given Gulf-side humidity and salt-air exposure similar to Clearwater's coastal properties.",
              },
            },
            {
              "@type": "Question",
              name: "Do you handle large-scale paver hardscaping in master-planned communities?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes -- we regularly service extensive paver installations common in Palm Harbor's golf course and waterfront developments.",
              },
            },
          ],
        },
      },
    },
  },
  // Wesley Chapel
  {
    slug: "wesley-chapel",
    name: "Wesley Chapel",
    state: "FL",
    meta: {
      lastUpdated: "August 2026",
      reviewer:
        "[INSERT REVIEWER NAME], Owner / Lead Technician -- 48 Years in Terrazzo Restoration",
    },
    images: {
      hero: "wesley-chapel-hero.jpg",
      aboutImage: "wesley-abt.jpg",
      terrazzoBeforeAfter: "wesley-chapel-concrete.jpg",
      neighborhoodMap: "wesley-chapel-map.png",
    },
    content: {
      intro: {
        paragraph1: `Wesley Chapel is one of Tampa Bay Area's fastest-growing communities, and its newer housing stock means the work here looks different than in Tampa Bay Area's older, terrazzo-heavy neighborhoods. Instead, Wesley Chapel projects lean heavily toward concrete driveway sealing and repair, and paver patio and hardscaping installation and maintenance -- the kind of work that comes with newer construction reaching its first decade or two of wear, rather than original mid-century terrazzo.`,
        paragraph2: `What we see most in Wesley Chapel: concrete driveway and patio sealing/repair, paver hardscaping maintenance, and marble or travertine care in newer custom home construction.`,
      },
      // quote: {
      //     text: `"[PLACEHOLDER -- replace with a real Wesley Chapel customer quote, ideally naming the neighborhood and service performed.]"`,
      //     author: `[Customer Name], [Neighborhood], Wesley Chapel`,
      //     note: `This is placeholder content only -- do not publish a fabricated testimonial. Replace with an actual review from a Wesley Chapel customer (pulled from Google Business Profile, Facebook, or a direct request) before this page goes live. Once real reviews are in place, consider adding Review or AggregateRating schema alongside them.`,
      // },
      statsTitle: "A Look at Wesley Chapel's Housing Numbers",
      statsIntro: `Wesley Chapel's growth curve looks nothing like Tampa Bay Area's older, terrazzo-heavy cities, and that shows up clearly in the housing data below -- it's a market defined far more by new-construction maintenance than by original flooring restoration.`,
      dataTable: [
        {
          dataPoint: "Median home construction year",
          figure: "2006",
          meaning: "Terrazzo-era stock present but less concentrated",
        },
        {
          dataPoint: "Share of homes built pre-1950",
          figure: "~1%",
          meaning: "Higher share = greater density of original poured terrazzo",
        },
        {
          dataPoint: "Total housing units",
          figure: "27,200+",
          meaning: "Market size for restoration and hardscaping demand",
        },
        {
          dataPoint: "Population",
          figure: "(est. 76,011+)",
          meaning: "General market sizing context",
        },
      ],
      // dataNote: `One of Tampa Bay Area's fastest-growing submarkets over the past two decades -- driveway and hardscaping maintenance demand outpaces terrazzo restoration here.`,
      signs: {
        title: "Why Newer Construction Still Needs Restoration Services",
        text: `A common misconception is that newer homes don't need restoration services for years -- but concrete driveways in fast-growing communities like Wesley Chapel are now reaching 10-20 years old in many neighborhoods, exactly the age range where the first sealing failures, hairline cracking, and paver joint-sand erosion typically appear. We're seeing a real wave of first-time restoration needs in Wesley Chapel as its earliest development phases mature. Note: precise housing-age and permit-volume figures for Wesley Chapel were not available from the sources checked during this session -- pull current building-permit data from Pasco County before publishing specific growth statistics.`,
      },
      considerations: {
        title: "Wesley Chapel-Specific Considerations",
        text: `Many Wesley Chapel neighborhoods feature builder-grade concrete driveways and patios that were never professionally sealed at installation -- a genuine gap we see often during initial assessments here. New construction in the area also frequently includes extensive paver hardscaping around pools and outdoor kitchens, which benefits from establishing a proper sealing schedule now rather than waiting until visible wear appears.`,
      },
      services: `Concrete - Hardscaping - Marble - Travertine - Terrazzo.`,
      pricing: {
        title: "Typical Investment Range -- Wesley Chapel (2026)",
        rows: [
          {
            service: "Terrazzo restoration",
            range: "$5-$12 per sq ft",
            factors: "Condition, aggregate exposure needed, gloss level",
          },
          {
            service: "Marble restoration",
            range: "$4-$10 per sq ft",
            factors: "Etching severity, polish level, sealing",
          },
          {
            service: "Travertine restoration",
            range: "$3-$7 per sq ft",
            factors: "Pitting/hole-filling needs, indoor vs. outdoor",
          },
          {
            service: "Concrete sealing/repair",
            range: "$1-$3 per sq ft sealing; $3-$7 per sq ft if resurfacing",
            factors: "Crack repair, staining, sealant type",
          },
          {
            service: "Paver/hardscaping maintenance",
            range: "$1.25-$3.50 per sq ft",
            factors: "Joint-sand replacement, sealing, cleaning",
          },
        ],
      },
      faq: {
        title: "Wesley Chapel: Your Questions Answered",
        items: [
          {
            q: "Since Wesley Chapel is newer construction, is terrazzo restoration relevant here?",
            a: "Less so than in Tampa Bay Area's older communities -- most Wesley Chapel projects involve concrete and hardscaping maintenance for newer homes, though we still handle terrazzo, marble, and travertine work as needed.",
          },
          {
            q: "My Wesley Chapel driveway is only about 10 years old -- does it really need sealing already?",
            a: "Often yes, especially if it was never professionally sealed at installation, which is common with builder-grade concrete -- 10-20 years is typically when the first sealing and crack issues appear.",
          },
          {
            q: "Do you handle new paver installations, or just restoration?",
            a: "We focus on restoration, repair, sealing, and maintenance for existing hardscaping and concrete -- for entirely new installation-only projects, ask us directly about current scope.",
          },
          {
            q: "What's the most common issue you find in Wesley Chapel driveways?",
            a: "Concrete that was never sealed at installation, now showing its first staining, hairline cracking, or surface wear as the neighborhood reaches 10-20 years old.",
          },
        ],
      },
      contact: {
        businessName: "Tampa Terrazzo",
        address: "1409 Sunnyhills Dr, Brandon, FL 33511",
        phone: "(813) 842-2255",
        website: "tampaterrazzo.com",
        serviceArea: "Wesley Chapel, FL and surrounding neighborhoods",
        mapCenter: "28.2364, -82.3243",
      },
      structuredData: {
        note: `Paste the FAQPage schema below into the <head> or end of the <body> of the live Wesley Chapel page.`,
        schema: {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "Since Wesley Chapel is newer construction, is terrazzo restoration relevant here?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Less so than in Tampa Bay Area's older communities -- most Wesley Chapel projects involve concrete and hardscaping maintenance for newer homes, though we still handle terrazzo, marble, and travertine work as needed.",
              },
            },
            {
              "@type": "Question",
              name: "My Wesley Chapel driveway is only about 10 years old -- does it really need sealing already?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Often yes, especially if it was never professionally sealed at installation, which is common with builder-grade concrete -- 10-20 years is typically when the first sealing and crack issues appear.",
              },
            },
            {
              "@type": "Question",
              name: "Do you handle new paver installations, or just restoration?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "We focus on restoration, repair, sealing, and maintenance for existing hardscaping and concrete -- for entirely new installation-only projects, ask us directly about current scope.",
              },
            },
            {
              "@type": "Question",
              name: "What's the most common issue you find in Wesley Chapel driveways?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Concrete that was never sealed at installation, now showing its first staining, hairline cracking, or surface wear as the neighborhood reaches 10-20 years old.",
              },
            },
          ],
        },
      },
    },
  },
  // Riverview
  {
    slug: "riverview",
    name: "Riverview",
    state: "FL",
    meta: {
      lastUpdated: "August 2026",
      reviewer:
        "[INSERT REVIEWER NAME], Owner / Lead Technician -- 48 Years in Terrazzo Restoration",
    },
    images: {
      hero: "riverview-hero.jpg",
      aboutImage: "riverview-abt.jpg",
      terrazzoBeforeAfter: "riverview-concrete.jpg",
      neighborhoodMap: "riverview-map.png",
    },
    content: {
      intro: {
        paragraph1: `Like Wesley Chapel, Riverview has seen explosive growth over the past two decades, and that newer housing stock shapes the kind of work we do here -- primarily concrete driveway and patio sealing, crack repair, and paver hardscaping for homes reaching the point where their original builder-grade finishes need real maintenance for the first time.`,
        paragraph2: `What we see most in Riverview: concrete sealing and crack repair on newer driveways, paver patio maintenance, and occasional marble or travertine work in custom-built homes.`,
      },
      // quote: {
      //     text: `"[PLACEHOLDER -- replace with a real Riverview customer quote, ideally naming the neighborhood and service performed.]"`,
      //     author: `[Customer Name], [Neighborhood], Riverview`,
      //     note: `This is placeholder content only -- do not publish a fabricated testimonial. Replace with an actual review from a Riverview customer (pulled from Google Business Profile, Facebook, or a direct request) before this page goes live. Once real reviews are in place, consider adding Review or AggregateRating schema alongside them.`,
      // },
      statsTitle: "Riverview Housing Data & What It Signals",
      statsIntro: `Riverview's explosive growth since the early 2000s means its housing data tells a very different story than St. Petersburg's or Largo's -- one where maintenance timing on newer surfaces matters more than uncovering original terrazzo.`,
      dataTable: [
        {
          dataPoint: "Median home construction year",
          figure: "2006",
          meaning: "Terrazzo-era stock present but less concentrated",
        },
        {
          dataPoint: "Share of homes built pre-1950",
          figure: "~0.4%",
          meaning: "Higher share = greater density of original poured terrazzo",
        },
        {
          dataPoint: "Total housing units",
          figure: "41,914",
          meaning: "Market size for restoration and hardscaping demand",
        },
        {
          dataPoint: "Population",
          figure: "116,251",
          meaning: "General market sizing context",
        },
      ],
      // dataNote: `Similar growth profile to Wesley Chapel; original construction is now reaching its first 10-20 year maintenance window.`,
      signs: {
        title:
          "Riverview's Rapid Growth and What It Means for Maintenance Timing",
        text: `Riverview's population and housing stock have expanded dramatically since the early 2000s, which means a large share of the area's concrete and paver installations are now reaching the same 10-20 year maintenance window as Wesley Chapel's earliest neighborhoods. We're increasingly fielding first-time sealing and repair requests from homeowners who assumed their relatively young home wouldn't need this kind of service yet. Note: precise housing-age figures for Riverview were not available from the sources checked during this session -- pull current data from Hillsborough County before publishing specific statistics.`,
      },
      considerations: {
        title: "Riverview-Specific Considerations",
        text: `Riverview's proximity to the Alafia River and surrounding waterways means some properties deal with higher ambient humidity and drainage considerations that affect how quickly unsealed concrete and pavers absorb moisture -- worth factoring into how soon after noticing early wear you should schedule a sealing assessment.`,
      },
      services: `Concrete - Hardscaping - Marble - Travertine - Terrazzo.`,
      pricing: {
        title: "Typical Investment Range -- Riverview (2026)",
        rows: [
          {
            service: "Terrazzo restoration",
            range: "$5-$12 per sq ft",
            factors: "Condition, aggregate exposure needed, gloss level",
          },
          {
            service: "Marble restoration",
            range: "$4-$10 per sq ft",
            factors: "Etching severity, polish level, sealing",
          },
          {
            service: "Travertine restoration",
            range: "$3-$7 per sq ft",
            factors: "Pitting/hole-filling needs, indoor vs. outdoor",
          },
          {
            service: "Concrete sealing/repair",
            range: "$1-$3 per sq ft sealing; $3-$7 per sq ft if resurfacing",
            factors: "Crack repair, staining, sealant type",
          },
          {
            service: "Paver/hardscaping maintenance",
            range: "$1.25-$3.50 per sq ft",
            factors: "Joint-sand replacement, sealing, cleaning",
          },
        ],
      },
      faq: {
        title: "Frequently Asked Riverview Questions",
        items: [
          {
            q: "What's the most common service you provide in Riverview?",
            a: "Concrete sealing and repair, along with paver hardscaping maintenance -- Riverview's newer housing stock means these services see the most demand compared to terrazzo restoration.",
          },
          {
            q: "How old does a Riverview driveway typically need to be before it needs sealing?",
            a: "Many Riverview driveways are now reaching the 10-20 year range where sealing and minor crack repair commonly become necessary for the first time, especially if never sealed at installation.",
          },
          {
            q: "Does Riverview's location near the river affect concrete maintenance?",
            a: "Proximity to waterways can mean higher ambient humidity in some areas, which is a factor we account for when recommending a sealing schedule.",
          },
          {
            q: "Do you work on custom-built homes in Riverview, not just production housing?",
            a: "Yes -- we handle marble, travertine, and terrazzo work for custom construction in Riverview alongside our more common concrete and hardscaping projects.",
          },
        ],
      },
      contact: {
        businessName: "Tampa Terrazzo",
        address: "1409 Sunnyhills Dr, Brandon, FL 33511",
        phone: "(813) 842-2255",
        website: "tampaterrazzo.com",
        serviceArea: "Riverview, FL and surrounding neighborhoods",
        mapCenter: "27.8661, -82.3241",
      },
      structuredData: {
        note: `Paste the FAQPage schema below into the <head> or end of the <body> of the live Riverview page.`,
        schema: {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "What's the most common service you provide in Riverview?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Concrete sealing and repair, along with paver hardscaping maintenance -- Riverview's newer housing stock means these services see the most demand compared to terrazzo restoration.",
              },
            },
            {
              "@type": "Question",
              name: "How old does a Riverview driveway typically need to be before it needs sealing?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Many Riverview driveways are now reaching the 10-20 year range where sealing and minor crack repair commonly become necessary for the first time, especially if never sealed at installation.",
              },
            },
            {
              "@type": "Question",
              name: "Does Riverview's location near the river affect concrete maintenance?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Proximity to waterways can mean higher ambient humidity in some areas, which is a factor we account for when recommending a sealing schedule.",
              },
            },
            {
              "@type": "Question",
              name: "Do you work on custom-built homes in Riverview, not just production housing?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes -- we handle marble, travertine, and terrazzo work for custom construction in Riverview alongside our more common concrete and hardscaping projects.",
              },
            },
          ],
        },
      },
    },
  },
  // Sarasota
  {
    slug: "sarasota",
    name: "Sarasota",
    state: "FL",
    meta: {
      lastUpdated: "August 2026",
      reviewer:
        "[INSERT REVIEWER NAME], Owner / Lead Technician -- 48 Years in Terrazzo Restoration",
    },
    images: {
      hero: "sarasota-hero.jpg",
      aboutImage: "sarasota-abt.jpg",
      terrazzoBeforeAfter: "sarasota-terrazzo.jpg",
      neighborhoodMap: "sarasota-map.png",
    },
    content: {
      intro: {
        paragraph1: `Sarasota sits at the outer edge of our regular service area, and we make the trip for projects across the city's mix of historic downtown properties, barrier-island homes on Siesta Key and Lido Key, and newer luxury construction. Sarasota's strong arts and design culture also means higher-end marble, travertine, and custom hardscaping work is common here, alongside terrazzo restoration in the city's older neighborhoods.`,
        paragraph2: `What we see most in Sarasota: marble and travertine restoration in higher-end homes and renovations, terrazzo restoration in Sarasota's historic districts, and custom paver hardscaping for waterfront properties.`,
      },
      // quote: {
      //     text: `"[PLACEHOLDER -- replace with a real Sarasota customer quote, ideally naming the neighborhood and service performed.]"`,
      //     author: `[Customer Name], [Neighborhood], Sarasota`,
      //     note: `This is placeholder content only -- do not publish a fabricated testimonial. Replace with an actual review from a Sarasota customer (pulled from Google Business Profile, Facebook, or a direct request) before this page goes live. Once real reviews are in place, consider adding Review or AggregateRating schema alongside them.`,
      // },
      statsTitle: "Sarasota's Housing Profile: A Closer Look",
      statsIntro: `Sarasota's median construction year runs later than our core Tampa Bay Area markets, which changes where original terrazzo tends to concentrate. The figures below reflect the most recent available U.S. Census / American Community Survey-derived data for the city and county.`,
      dataTable: [
        {
          dataPoint: "Median home construction year",
          figure: "1982",
          meaning: "Terrazzo-era stock present but less concentrated",
        },
        {
          dataPoint: "Share of homes built pre-1950",
          figure: "~2.8%",
          meaning: "Higher share = greater density of original poured terrazzo",
        },
        {
          dataPoint: "Total housing units",
          figure: "298,816 (county) / 33,584 (city)",
          meaning: "Market size for restoration and hardscaping demand",
        },
        {
          dataPoint: "Population",
          figure: "516,517 (county) / 60,756 (city)",
          meaning: "General market sizing context",
        },
      ],
      // dataNote: `Later median construction year than Tampa Bay Area's core service areas -- terrazzo restoration demand concentrates in the historic downtown core rather than citywide.`,
      signs: {
        title: "Why Sarasota's Projects Skew Toward Higher-End Finishes",
        text: `Sarasota's citywide median home construction year is 1982, and countywide it's 1990 -- both noticeably later than St. Petersburg or Largo, meaning original terrazzo here is concentrated in specific historic pockets (downtown, parts of the barrier islands) rather than spread evenly across the market. Sarasota's design-forward reputation and concentration of custom and luxury construction means we see a higher proportion of premium finish requests here than in much of our core service area -- high-gloss marble polishing, decorative travertine finishes, and elaborate paver hardscaping designs around pools and outdoor living spaces. Barrier-island properties on Siesta Key and Lido Key add another layer of consideration, given direct Gulf exposure similar to Clearwater's beachfront communities.`,
      },
      considerations: {
        title: "Sarasota-Specific Considerations",
        text: `Because Sarasota is further from our Brandon home base than our other 8 service areas, scheduling for Sarasota projects sometimes works on a slightly different timeline than closer cities -- we recommend booking Sarasota assessments a bit further in advance where possible, particularly for larger or multi-stage restoration projects.`,
      },
      services: `Terrazzo - Marble - Travertine - Concrete - Hardscaping.`,
      pricing: {
        title: "Typical Investment Range -- Sarasota (2026)",
        rows: [
          {
            service: "Terrazzo restoration",
            range: "$6-$14 per sq ft",
            factors: "Condition, aggregate exposure needed, gloss level",
          },
          {
            service: "Marble restoration",
            range: "$5-$11.50 per sq ft",
            factors: "Etching severity, polish level, sealing",
          },
          {
            service: "Travertine restoration",
            range: "$3.50-$8 per sq ft",
            factors: "Pitting/hole-filling needs, indoor vs. outdoor",
          },
          {
            service: "Concrete sealing/repair",
            range:
              "$1-$3.50 per sq ft sealing; $3.50-$8 per sq ft if resurfacing",
            factors: "Crack repair, staining, sealant type",
          },
          {
            service: "Paver/hardscaping maintenance",
            range: "$1.50-$4 per sq ft",
            factors: "Joint-sand replacement, sealing, cleaning",
          },
        ],
      },
      faq: {
        title: "What Sarasota Clients Want to Know",
        items: [
          {
            q: "Do you regularly travel to Sarasota for projects, or only occasionally?",
            a: "We serve Sarasota as part of our regular Tampa Bay Area coverage area -- it's a further drive than our core service area, so scheduling may work slightly differently than for closer cities; call to confirm availability for your project.",
          },
          {
            q: "Do you service Siesta Key and Lido Key specifically?",
            a: "Yes -- barrier-island properties are part of our Sarasota coverage, with the same coastal-exposure sealing considerations we apply to other Gulf-side communities.",
          },
          {
            q: "Is Sarasota restoration work typically higher-end than other areas?",
            a: "We see a higher share of premium finish requests in Sarasota given the area's design-forward, custom-construction character, though we handle projects of every scope.",
          },
          {
            q: "How far in advance should I book a Sarasota assessment?",
            a: "Given the distance from our home base, we recommend reaching out a bit earlier than you might for closer cities, especially for larger projects -- call to check current scheduling.",
          },
          {
            q: "Is original terrazzo as common in Sarasota as in St. Petersburg or Tampa?",
            a: "Less so citywide -- Sarasota's median home construction year (1982) is noticeably later than St. Petersburg's (1969), so original terrazzo is concentrated in specific historic pockets like downtown Sarasota rather than spread evenly across the market.",
          },
        ],
      },
      contact: {
        businessName: "Tampa Terrazzo",
        address: "1409 Sunnyhills Dr, Brandon, FL 33511",
        phone: "(813) 842-2255",
        website: "tampaterrazzo.com",
        serviceArea: "Sarasota, FL and surrounding neighborhoods",
        mapCenter: "27.3364, -82.5307",
      },
      structuredData: {
        note: `Paste the FAQPage schema below into the <head> or end of the <body> of the live Sarasota page.`,
        schema: {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "Do you regularly travel to Sarasota for projects, or only occasionally?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "We serve Sarasota as part of our regular Tampa Bay Area coverage area -- it's a further drive than our core service area, so scheduling may work slightly differently than for closer cities; call to confirm availability for your project.",
              },
            },
            {
              "@type": "Question",
              name: "Do you service Siesta Key and Lido Key specifically?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes -- barrier-island properties are part of our Sarasota coverage, with the same coastal-exposure sealing considerations we apply to other Gulf-side communities.",
              },
            },
            {
              "@type": "Question",
              name: "Is Sarasota restoration work typically higher-end than other areas?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "We see a higher share of premium finish requests in Sarasota given the area's design-forward, custom-construction character, though we handle projects of every scope.",
              },
            },
            {
              "@type": "Question",
              name: "How far in advance should I book a Sarasota assessment?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Given the distance from our home base, we recommend reaching out a bit earlier than you might for closer cities, especially for larger projects -- call to check current scheduling.",
              },
            },
            {
              "@type": "Question",
              name: "Is original terrazzo as common in Sarasota as in St. Petersburg or Tampa?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Less so citywide -- Sarasota's median home construction year (1982) is noticeably later than St. Petersburg's (1969), so original terrazzo is concentrated in specific historic pockets like downtown Sarasota rather than spread evenly across the market.",
              },
            },
          ],
        },
      },
    },
  },
  // Bradenton
  {
    slug: "bradenton",
    name: "Bradenton",
    state: "FL",
    meta: {
      lastUpdated: "August 2026",
      reviewer: "Lead Technician -- 48 Years in Terrazzo Restoration",
    },
    images: {
      hero: "bradenton-abt.jpg",
      aboutImage: "bradenton-abt.jpg",
      terrazzoBeforeAfter: "bradenton-abt.jpg",
      neighborhoodMap: "bradenton-abt.jpg",
    },
    content: {
      intro: {
        paragraph1: `Bradenton combines historic riverfront neighborhoods along the Manatee River, classic 1950s and 60s residential subdivisions, and coastal communities near Anna Maria Island. From vintage mid-century poured terrazzo hidden under older carpet in West Bradenton to travertine pool decks in Palma Sola and Lakewood Ranch, we deliver specialized restoration crafted for Manatee County's subtropical climate.`,
        paragraph2: `What we see most in Bradenton: vintage poured terrazzo restoration in mid-century homes, travertine pool deck cleaning and sealing, and durable concrete and paver hardscaping.`,
      },
      statsTitle: "Bradenton Housing Data at a Glance",
      statsIntro: `Bradenton's housing stock features a strong concentration of mid-century single-family homes alongside historic downtown properties and newer planned communities.`,
      dataTable: [
        {
          dataPoint: "Median home construction year",
          figure: "1983",
          meaning:
            "Substantial terrazzo-era mid-century stock across established neighborhoods",
        },
        {
          dataPoint: "Share of homes built pre-1970",
          figure: "~24.8%",
          meaning:
            "Strong presence of authentic poured terrazzo under carpet and tile",
        },
        {
          dataPoint: "Total housing units",
          figure: "26,140",
          meaning: "Active residential restoration and stone surface market",
        },
        {
          dataPoint: "Population",
          figure: "~55,698",
          meaning: "Manatee County's historic urban center and coastal hub",
        },
      ],
      signs: {
        title: "Why Dedicated Restoration Matters in Bradenton",
        text: `Bradenton's proximity to the Manatee River and Gulf coast brings elevated ambient humidity and salt air exposure. Rather than applying topical waxes that cloud or blister in moisture, our diamond grinding and breathable sealing methods preserve the stone from within without trapping moisture.`,
      },
      considerations: {
        title: "Bradenton-Specific Considerations",
        text: `Older homes in West Bradenton, Riverview Boulevard, and downtown frequently harbor original poured terrazzo beneath aged carpet or laminate. Meanwhile, pool decks and outdoor living areas across East Bradenton and Lakewood Ranch benefit from specialized travertine and paver joint stabilization and penetrating sealers.`,
      },
      services: `Terrazzo - Marble - Travertine - Concrete - Hardscaping.`,
      pricing: {
        title: "Typical Investment Range -- Bradenton (2026)",
        rows: [
          {
            service: "Terrazzo restoration",
            range: "$5-$12 per sq ft",
            factors: "Condition, aggregate exposure needed, gloss level",
          },
          {
            service: "Marble restoration",
            range: "$4-$10 per sq ft",
            factors: "Etching severity, polish level, sealing",
          },
          {
            service: "Travertine restoration",
            range: "$3-$7 per sq ft",
            factors: "Pitting/hole-filling needs, indoor vs. outdoor",
          },
          {
            service: "Concrete sealing/repair",
            range: "$1-$3 per sq ft sealing; $3-$7 per sq ft if resurfacing",
            factors: "Crack repair, staining, sealant type",
          },
          {
            service: "Paver/hardscaping maintenance",
            range: "$1.25-$3.50 per sq ft",
            factors: "Joint-sand replacement, sealing, cleaning",
          },
        ],
      },
      faq: {
        title: "Answers to Bradenton's Most Common Questions",
        items: [
          {
            q: "Do you service residential homes throughout Bradenton and Manatee County?",
            a: "Yes -- we regularly restore floors, patios, and surfaces across West Bradenton, Riverview Blvd, Palma Sola, Lakewood Ranch, and surrounding areas.",
          },
          {
            q: "Can carpet tack-strip holes in old Bradenton terrazzo be repaired invisibly?",
            a: "Yes -- we custom-match aggregate stone chips and tinted matrix resin to match your floor's original 1950s or 60s mix.",
          },
          {
            q: "Do you restore travertine pool decks and lanais in Bradenton?",
            a: "Yes -- we clean, fill worn voids, diamond-hone, and apply breathable penetrating sealers suited for Florida humidity and pool water.",
          },
          {
            q: "How do I get an estimate for my Bradenton project?",
            a: "Call us at (813) 842-2255 or request an estimate online to schedule a free on-site assessment.",
          },
        ],
      },
      contact: {
        businessName: "Tampa Terrazzo",
        address: "1409 Sunnyhills Dr, Brandon, FL 33511",
        phone: "(813) 842-2255",
        website: "tampaterrazzo.com",
        serviceArea: "Bradenton, FL and surrounding Manatee County communities",
        mapCenter: "27.4989, -82.5748",
      },
      structuredData: {
        note: `Paste the FAQPage schema below into the <head> or end of the <body> of the live Bradenton page.`,
        schema: {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "Do you service residential homes throughout Bradenton and Manatee County?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes -- we regularly restore floors, patios, and surfaces across West Bradenton, Riverview Blvd, Palma Sola, Lakewood Ranch, and surrounding areas.",
              },
            },
            {
              "@type": "Question",
              name: "Can carpet tack-strip holes in old Bradenton terrazzo be repaired invisibly?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes -- we custom-match aggregate stone chips and tinted matrix resin to match your floor's original 1950s or 60s mix.",
              },
            },
            {
              "@type": "Question",
              name: "Do you restore travertine pool decks and lanais in Bradenton?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes -- we clean, fill worn voids, diamond-hone, and apply breathable penetrating sealers suited for Florida humidity and pool water.",
              },
            },
            {
              "@type": "Question",
              name: "How do I get an estimate for my Bradenton project?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Call us at (813) 842-2255 or request an estimate online to schedule a free on-site assessment.",
              },
            },
          ],
        },
      },
    },
  },
  // Dunedin
  {
    slug: "dunedin",
    name: "Dunedin",
    state: "FL",
    meta: {
      lastUpdated: "August 2026",
      reviewer: "Lead Technician -- 48 Years in Terrazzo Restoration",
    },
    images: {
      hero: "dunedin-abt.jpg",
      aboutImage: "dunedin-abt.jpg",
      terrazzoBeforeAfter: "dunedin-abt.jpg",
      neighborhoodMap: "dunedin-abt.jpg",
    },
    content: {
      intro: {
        paragraph1: `Dunedin is celebrated for its historic charm, walkable coastal village feel, and rich collection of 1950s and 1960s Florida ranch and coastal cottage homes. Poured terrazzo was the premier flooring choice throughout Dunedin during this post-war construction surge, and decades later, these timeless floors are waiting to be uncovered, restored, and polished back to life.`,
        paragraph2: `What we see most in Dunedin: authentic mid-century terrazzo floor restoration under carpet and laminate, marble entryway refinishing, and patio/driveway concrete restoration near the Gulf coast.`,
      },
      statsTitle: "Dunedin Housing Data at a Glance",
      statsIntro: `Dunedin features one of northern Pinellas County's highest concentrations of mid-century residential architecture.`,
      dataTable: [
        {
          dataPoint: "Median home construction year",
          figure: "1973",
          meaning: "Peak terrazzo installation era in Pinellas County",
        },
        {
          dataPoint: "Share of homes built pre-1970",
          figure: "~41.2%",
          meaning: "Very high probability of original poured terrazzo floors",
        },
        {
          dataPoint: "Total housing units",
          figure: "21,580",
          meaning: "High concentration of historic and coastal properties",
        },
        {
          dataPoint: "Population",
          figure: "~36,068",
          meaning: "Vibrant coastal Pinellas County community",
        },
      ],
      signs: {
        title: "Preserving Dunedin's Architectural Heritage",
        text: `Dunedin homeowners appreciate authentic craftsmanship and historic preservation. Our mechanical diamond polishing process never relies on wax or topical coats. Instead, we grind down surface damage, repair old tack-strip holes with custom aggregate blends, and buff the natural marble chips to a lasting shine.`,
      },
      considerations: {
        title: "Dunedin-Specific Considerations",
        text: `Coastal breezes and proximity to St. Joseph Sound make moisture management vital. We perform moisture testing before applying breathable penetrating sealers that protect against sandy grit tracked in from Honeymoon Island and the Dunedin Causeway.`,
      },
      services: `Terrazzo - Marble - Travertine - Concrete - Hardscaping.`,
      pricing: {
        title: "Typical Investment Range -- Dunedin (2026)",
        rows: [
          {
            service: "Terrazzo restoration",
            range: "$5-$12 per sq ft",
            factors: "Condition, aggregate exposure needed, gloss level",
          },
          {
            service: "Marble restoration",
            range: "$4-$10 per sq ft",
            factors: "Etching severity, polish level, sealing",
          },
          {
            service: "Travertine restoration",
            range: "$3-$7 per sq ft",
            factors: "Pitting/hole-filling needs, indoor vs. outdoor",
          },
          {
            service: "Concrete sealing/repair",
            range: "$1-$3 per sq ft sealing; $3-$7 per sq ft if resurfacing",
            factors: "Crack repair, staining, sealant type",
          },
          {
            service: "Paver/hardscaping maintenance",
            range: "$1.25-$3.50 per sq ft",
            factors: "Joint-sand replacement, sealing, cleaning",
          },
        ],
      },
      faq: {
        title: "Answers to Dunedin's Most Common Questions",
        items: [
          {
            q: "Is original terrazzo common in Dunedin homes?",
            a: "Yes -- with over 40% of Dunedin's housing built before 1970, original poured terrazzo floors are common beneath carpet, linoleum, and tile.",
          },
          {
            q: "How long does a typical home terrazzo restoration take in Dunedin?",
            a: "Most residential terrazzo projects take 3 to 5 days from initial grinding and patch matching through final diamond polishing and breathable sealing.",
          },
          {
            q: "Do you use waxes or topical coatings on Dunedin floors?",
            a: "Never. The shine comes directly from the stone itself via mechanical diamond polishing, eliminating peeling, yellowing, or periodic stripping.",
          },
          {
            q: "Do you offer free estimates in Dunedin?",
            a: "Yes -- we provide free on-site assessments throughout Dunedin and northern Pinellas County.",
          },
        ],
      },
      contact: {
        businessName: "Tampa Terrazzo",
        address: "1409 Sunnyhills Dr, Brandon, FL 33511",
        phone: "(813) 842-2255",
        website: "tampaterrazzo.com",
        serviceArea: "Dunedin, FL and surrounding Pinellas County communities",
        mapCenter: "28.0197, -82.7718",
      },
      structuredData: {
        note: `Paste the FAQPage schema below into the <head> or end of the <body> of the live Dunedin page.`,
        schema: {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "Is original terrazzo common in Dunedin homes?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes -- with over 40% of Dunedin's housing built before 1970, original poured terrazzo floors are common beneath carpet, linoleum, and tile.",
              },
            },
            {
              "@type": "Question",
              name: "How long does a typical home terrazzo restoration take in Dunedin?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Most residential terrazzo projects take 3 to 5 days from initial grinding and patch matching through final diamond polishing and breathable sealing.",
              },
            },
            {
              "@type": "Question",
              name: "Do you use waxes or topical coatings on Dunedin floors?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Never. The shine comes directly from the stone itself via mechanical diamond polishing, eliminating peeling, yellowing, or periodic stripping.",
              },
            },
            {
              "@type": "Question",
              name: "Do you offer free estimates in Dunedin?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes -- we provide free on-site assessments throughout Dunedin and northern Pinellas County.",
              },
            },
          ],
        },
      },
    },
  },
  // Lakeland
  {
    slug: "lakeland",
    name: "Lakeland",
    state: "FL",
    meta: {
      lastUpdated: "August 2026",
      reviewer: "Lead Technician -- 48 Years in Terrazzo Restoration",
    },
    images: {
      hero: "lakeland-abt.jpg",
      aboutImage: "lakeland-abt.jpg",
      terrazzoBeforeAfter: "lakeland-abt.jpg",
      neighborhoodMap: "lakeland-abt.jpg",
    },
    content: {
      intro: {
        paragraph1: `Lakeland boasts a rich architectural heritage, from historic brick districts and mid-century homes surrounding Lake Hollingsworth and Lake Morton to established neighborhoods throughout Polk County. Original poured terrazzo floors from the 1950s through 1970s are widespread across Lakeland, offering enduring durability and beauty when restored by experienced craftsmen.`,
        paragraph2: `What we see most in Lakeland: historic mid-century terrazzo revival, travertine patio restoration, and concrete cleaning, repair, and sealing across historic and suburban properties.`,
      },
      statsTitle: "Lakeland Housing Data at a Glance",
      statsIntro: `Lakeland combines historic preservation districts with expansive post-war residential subdivisions where poured terrazzo remains a cornerstone feature.`,
      dataTable: [
        {
          dataPoint: "Median home construction year",
          figure: "1982",
          meaning:
            "Extensive mid-century housing stock in established central neighborhoods",
        },
        {
          dataPoint: "Share of homes built pre-1970",
          figure: "~27.4%",
          meaning:
            "Significant inventory of original mid-century terrazzo floors",
        },
        {
          dataPoint: "Total housing units",
          figure: "52,800",
          meaning: "Polk County's largest metropolitan center",
        },
        {
          dataPoint: "Population",
          figure: "~115,425",
          meaning:
            "Growing community with strong appreciation for historic home preservation",
        },
      ],
      signs: {
        title: "Lakeland's Historic Character Deserves Authentic Craftsmanship",
        text: `Many older Lakeland homes have original terrazzo concealed under tile, vinyl, or carpet from past renovations. Our dustless diamond grinding system exposes the original stone without creating a mess, restoring broken chips and hairline cracks with handcrafted precision passed down since 1978.`,
      },
      considerations: {
        title: "Lakeland-Specific Considerations",
        text: `Lakeland's interior lake-rich terrain experiences intense summer heat and sudden rains. We tailor our penetrating sealers to resist moisture vapor transmission and UV exposure, keeping both interior terrazzo and exterior hardscapes protected year-round.`,
      },
      services: `Terrazzo - Marble - Travertine - Concrete - Hardscaping.`,
      pricing: {
        title: "Typical Investment Range -- Lakeland (2026)",
        rows: [
          {
            service: "Terrazzo restoration",
            range: "$5-$12 per sq ft",
            factors: "Condition, aggregate exposure needed, gloss level",
          },
          {
            service: "Marble restoration",
            range: "$4-$10 per sq ft",
            factors: "Etching severity, polish level, sealing",
          },
          {
            service: "Travertine restoration",
            range: "$3-$7 per sq ft",
            factors: "Pitting/hole-filling needs, indoor vs. outdoor",
          },
          {
            service: "Concrete sealing/repair",
            range: "$1-$3 per sq ft sealing; $3-$7 per sq ft if resurfacing",
            factors: "Crack repair, staining, sealant type",
          },
          {
            service: "Paver/hardscaping maintenance",
            range: "$1.25-$3.50 per sq ft",
            factors: "Joint-sand replacement, sealing, cleaning",
          },
        ],
      },
      faq: {
        title: "Answers to Lakeland's Most Common Questions",
        items: [
          {
            q: "Do you travel to Lakeland for residential and commercial projects?",
            a: "Yes -- Lakeland and greater Polk County are part of our standard service area, with regular trips for terrazzo, travertine, and concrete restoration.",
          },
          {
            q: "Can old carpet glue and tile mortar be removed from Lakeland terrazzo?",
            a: "Yes -- our heavy-duty planetary diamond grinders strip away hardened mastic, adhesives, and mortar beds without gouging the original terrazzo.",
          },
          {
            q: "Are crack repairs noticeable after restoration?",
            a: "We hand-blend matching marble aggregate chips and tint matrix resins to mirror the surrounding floor so repairs disappear into the natural pattern.",
          },
          {
            q: "How do I schedule a free terrazzo estimate in Lakeland?",
            a: "Call us at (813) 842-2255 or submit an online request for a free on-site consultation.",
          },
        ],
      },
      contact: {
        businessName: "Tampa Terrazzo",
        address: "1409 Sunnyhills Dr, Brandon, FL 33511",
        phone: "(813) 842-2255",
        website: "tampaterrazzo.com",
        serviceArea: "Lakeland, FL and surrounding Polk County communities",
        mapCenter: "28.0395, -81.9498",
      },
      structuredData: {
        note: `Paste the FAQPage schema below into the <head> or end of the <body> of the live Lakeland page.`,
        schema: {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "Do you travel to Lakeland for residential and commercial projects?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes -- Lakeland and greater Polk County are part of our standard service area, with regular trips for terrazzo, travertine, and concrete restoration.",
              },
            },
            {
              "@type": "Question",
              name: "Can old carpet glue and tile mortar be removed from Lakeland terrazzo?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes -- our heavy-duty planetary diamond grinders strip away hardened mastic, adhesives, and mortar beds without gouging the original terrazzo.",
              },
            },
            {
              "@type": "Question",
              name: "Are crack repairs noticeable after restoration?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "We hand-blend matching marble aggregate chips and tint matrix resins to mirror the surrounding floor so repairs disappear into the natural pattern.",
              },
            },
            {
              "@type": "Question",
              name: "How do I schedule a free terrazzo estimate in Lakeland?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Call us at (813) 842-2255 or submit an online request for a free on-site consultation.",
              },
            },
          ],
        },
      },
    },
  },
];
